import Phaser from 'phaser';
import { sfx } from '../audio/SFX.js';
import { isCalmMode } from '../audio/CalmMode.js';

interface AnnouncerEntry {
  text: Phaser.GameObjects.Text;
  createdAt: number;
}

const STYLE_CONFIG: Record<string, { color: string; fontSize: string; shake: number }> = {
  kill_streak: { color: '#ff4444', fontSize: '42px', shake: 0.012 },
  first_blood: { color: '#ff0000', fontSize: '48px', shake: 0.015 },
  extraction: { color: '#00ff88', fontSize: '36px', shake: 0.005 },
  shutdown: { color: '#ffaa00', fontSize: '38px', shake: 0.01 },
};

/** Full-screen Dota-style announcer overlay */
export class AnnouncerOverlay {
  private scene: Phaser.Scene;
  private queue: AnnouncerEntry[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Map style to sound key */
  private static STYLE_SOUNDS: Record<string, string> = {
    first_blood: 'first_blood',
    shutdown: 'kill_confirmed',
    extraction: 'extraction_flash',
  };

  /** Map style to voiceline key */
  private static STYLE_VOICES: Record<string, string> = {
    first_blood: 'voice_first_blood',
  };

  /** Show an announcer message — big, centered, dramatic */
  announce(message: string, style: string): void {
    // Calm Mode: suppress the dramatic combat callouts (DOUBLE KILL, FIRST
    // BLOOD, SHUTDOWN) — text, slam-in, sound + voiceline. Keep informational
    // 'extraction'-style notices (e.g. COIN FRENZY!) so the player isn't blind.
    if (isCalmMode() && style !== 'extraction') return;

    const soundKey = AnnouncerOverlay.STYLE_SOUNDS[style] ?? 'kill_confirmed';
    sfx(this.scene, soundKey);
    // Play voiceline if available
    const voiceKey = AnnouncerOverlay.STYLE_VOICES[style];
    if (voiceKey) sfx(this.scene, voiceKey);

    const { width, height } = this.scene.scale;
    const config = STYLE_CONFIG[style] ?? STYLE_CONFIG.kill_streak;

    // Screen shake
    this.scene.cameras.main.shake(250, config.shake);

    // Main text
    const text = this.scene.add.text(width / 2, height * 0.45, message, {
      fontFamily: 'monospace',
      fontSize: config.fontSize,
      color: config.color,
      stroke: '#000000',
      strokeThickness: 6,
      fontStyle: 'bold',
    }).setOrigin(0.5).setScrollFactor(0).setDepth(500).setScale(0.1).setAlpha(0);

    // Slam in animation
    this.scene.tweens.add({
      targets: text,
      scale: 1.1,
      alpha: 1,
      duration: 200,
      ease: 'Back.easeOut',
      onComplete: () => {
        // Hold + slight pulse
        this.scene.tweens.add({
          targets: text,
          scale: 1.0,
          duration: 100,
          onComplete: () => {
            // Fade out
            this.scene.tweens.add({
              targets: text,
              alpha: 0,
              scale: 0.9,
              y: text.y - 20,
              duration: 800,
              delay: 600,
              ease: 'Power2',
              onComplete: () => text.destroy(),
            });
          },
        });
      },
    });

    // Background flash
    this.scene.cameras.main.flash(150, 255, 100, 100, false);
  }

  /** Map kill count to streak sound */
  private static STREAK_SOUNDS: Record<number, string> = {
    2: 'streak_double',
    3: 'streak_triple',
    4: 'streak_ultra',
    5: 'streak_rampage',
  };

  /** Map kill count to voiceline */
  private static STREAK_VOICES: Record<number, string> = {
    2: 'voice_double_kill',
    3: 'voice_triple_kill',
  };

  /** Show kill streak with killer name subtitle */
  announceKillStreak(
    message: string,
    playerName: string,
    killCount: number,
    bonusCoins: number
  ): void {
    if (isCalmMode()) return; // no-stimulus: skip kill-streak callouts entirely
    const streakSound = AnnouncerOverlay.STREAK_SOUNDS[killCount] ?? 'streak_godlike';
    sfx(this.scene, streakSound);
    // Play voiceline for this streak level (dominating for 4+)
    const voiceKey = AnnouncerOverlay.STREAK_VOICES[killCount] ?? (killCount >= 4 ? 'voice_dominating' : '');
    if (voiceKey) sfx(this.scene, voiceKey);
    this.announce(message, 'kill_streak');

    const { width, height } = this.scene.scale;

    // Subtitle with player name and bonus
    const subtitle = this.scene.add.text(
      width / 2,
      height * 0.45 + 50,
      `${playerName} — ${killCount} kills (+${bonusCoins} bonus)`,
      {
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#cccccc',
        stroke: '#000000',
        strokeThickness: 3,
      }
    ).setOrigin(0.5).setScrollFactor(0).setDepth(500).setAlpha(0);

    this.scene.tweens.add({
      targets: subtitle,
      alpha: 1,
      duration: 300,
      delay: 200,
      onComplete: () => {
        this.scene.tweens.add({
          targets: subtitle,
          alpha: 0,
          duration: 600,
          delay: 800,
          onComplete: () => subtitle.destroy(),
        });
      },
    });
  }
}
